import type { ReactNode } from 'react'
import {
  type Dispatch,
  type SetStateAction,
  memo,
  useCallback,
  useMemo,
  useState,
} from 'react'

import type {
  ColumnDef,
  Header,
  InitialTableState,
  PaginationState,
  SortingState,
} from '@tanstack/react-table'
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'

import ChevronBottomIcon from '@/assets/icons/icon-chevron-bottom.svg'
import { EmptySlot } from '@/components/ui/empty/empty-provider.tsx'
import { EmptyTableContent } from '@/components/ui/empty/empty-table-content.tsx'
import { SearchInput } from '@/components/ui/search-input.tsx'
import { Skeleton } from '@/components/ui/skeleton'
import { classNames } from '@/utils/helpers'

import type { ExportProps } from '../../export.tsx'
import { Export } from '../../export.tsx'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './../table.tsx'
import { DataTablePagination } from './TablePagination'

export enum PaginationTypes {
  INFINITE = 'infinite',
  SIZE_SELECT = 'pageSelect',
  DEFAULT = 'default',
}

type DataTableProps<TData, TValue> = {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  cellClassName?: string
  emptyClassName?: string
  initialState?: InitialTableState
  title?: string | ReactNode
  titleClassName?: string
  enableSearch?: boolean
  searchColumn?: string
  enablePagination?: boolean
  enableSorting?: boolean
  stickyHeaderSize?: string
  paginationState?: PaginationState
  onPaginationChange?: Dispatch<SetStateAction<PaginationState>>
  sortingState?: SortingState
  onSortingChange?: Dispatch<SetStateAction<SortingState>>
  className?: string
  pageCount?: number
  hasActionColumn?: boolean | undefined
  extra?: ReactNode
  wrapperClassName?: string
  footer?: ReactNode
  footerRow?: ReactNode
  paginationVariant?: PaginationTypes
  tbodyMinHeight?: boolean
  isLoading?: boolean
  searchQuery?: string
  testId?: string
} & (
  | { enableExport?: undefined; exportOptions?: undefined }
  | {
      enableExport: true
      exportOptions: ExportProps
    }
)

/**
 * This component is used for controlled pagination (Todo: later implement logic in shared component)
 */
const _DataTable = <TData, TValue>({
  columns,
  data,
  cellClassName,
  initialState,
  title,
  titleClassName,
  enableSearch = false,
  searchColumn = '',
  enablePagination = false,
  enableSorting = false,
  stickyHeaderSize,
  paginationState,
  onPaginationChange,
  className,
  pageCount,
  hasActionColumn,
  enableExport,
  exportOptions,
  extra,
  onSortingChange,
  sortingState,
  wrapperClassName,
  emptyClassName,
  footer,
  footerRow,
  paginationVariant = PaginationTypes.SIZE_SELECT,
  tbodyMinHeight = true,
  isLoading,
  searchQuery,
  testId,
}: DataTableProps<TData, TValue>) => {
  const [sorting, setSorting] = useState<SortingState>(sortingState || [])

  const table = useReactTable({
    data,
    columns,
    initialState,
    enableSortingRemoval: true,
    getCoreRowModel: getCoreRowModel(),
    ...(enableSearch && { getFilteredRowModel: getFilteredRowModel() }),
    ...(enablePagination && {
      getPaginationRowModel: getPaginationRowModel(),
      onPaginationChange: onPaginationChange,
      manualPagination: true,
      pageCount: pageCount,
    }),
    ...(enableSorting && {
      getSortedRowModel: getSortedRowModel(),
      onSortingChange: handleSortingChange,
    }),
    state: {
      ...(enablePagination && { pagination: paginationState }),
      ...(enableSorting && { sorting }),
      ...(enableSearch && {
        globalFilter: searchQuery,
      }),
    },
  })

  function handleSortingChange(sorting: SetStateAction<SortingState>) {
    setSorting(sorting)
    onSortingChange && onSortingChange(sorting)
  }

  const handleHeaderClick = useCallback(
    <TData, TValue>(header: Header<TData, TValue>) =>
      () => {
        if (enableSorting && header.column.getCanSort()) {
          header.column.toggleSorting()
        }
      },
    [enableSorting],
  )

  // Renders the table header based on the header groups and headers of the table.
  const renderHeader = useMemo(
    () => {
      return table.getHeaderGroups().map((headerGroup) => (
        <TableRow key={headerGroup.id}>
          {headerGroup.headers.map((header, index) => {
            return (
              <TableHead
                key={header.id}
                onClick={handleHeaderClick(header)}
                className={classNames({
                  'w-full': header.id.endsWith('-full'),
                  'cursor-pointer select-none':
                    header.column.getCanSort() && enableSorting,
                  'pr-6 text-right': hasActionColumn && header.id === 'actions',
                  cellClassName,
                })}
              >
                <div
                  className={classNames('flex items-center', {
                    'justify-end': index === headerGroup.headers.length - 1,
                  })}
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                  {header.column.getCanSort() && enableSorting && (
                    <div className="ml-2 flex flex-col items-center">
                      <ChevronBottomIcon
                        className={classNames(
                          'stroke-secondary-500 dark:stroke-secondary-100 h-[8px] w-[16px] rotate-180',
                          {
                            'stroke-primary-500 dark:stroke-primary-500':
                              header.column.getIsSorted() === 'asc',
                          },
                        )}
                      />
                      <ChevronBottomIcon
                        className={classNames(
                          'stroke-secondary-500 dark:stroke-secondary-100 h-[8px] w-[16px]',
                          {
                            'stroke-primary-500 dark:stroke-primary-500':
                              header.column.getIsSorted() === 'desc',
                          },
                        )}
                      />
                    </div>
                  )}
                </div>
              </TableHead>
            )
          })}
        </TableRow>
      ))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      table,
      handleHeaderClick,
      enableSorting,
      hasActionColumn,
      cellClassName,
      sorting,
    ],
  )

  const rows = table.getRowModel().rows

  // Renders the table rows based on the row model of the table.
  // If there are no rows in the model, it renders a single row indicating no results.
  const renderRows = useMemo(
    () => {
      if (rows.length === 0) {
        return (
          <TableRow>
            <TableCell
              colSpan={columns.length}
              className="h-24 text-center"
            >
              <EmptySlot>
                {() => (
                  <EmptyTableContent className={emptyClassName}>
                    No results.
                  </EmptyTableContent>
                )}
              </EmptySlot>
            </TableCell>
          </TableRow>
        )
      }

      return rows.map((row) => {
        return (
          <TableRow
            key={row.id}
            data-state={row.getIsSelected() && 'selected'}
            className={classNames({
              'w-full': row.id.endsWith('-full'),
            })}
          >
            {row?.getVisibleCells().map((cell) => {
              return (
                <TableCell
                  key={cell.id}
                  className={cellClassName}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              )
            })}
          </TableRow>
        )
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columns, data, paginationState, sorting, searchQuery, rows],
  )

  const renderLoadingSkeleton = useMemo(() => {
    return Array.from({ length: 10 }).map(() =>
      table.getHeaderGroups().map((headerGroup) => (
        <TableRow key={headerGroup.id}>
          {headerGroup.headers.map((header) => {
            const meta = header.column.columnDef.meta
            const isActionColumn = meta?.isActionColumn

            return (
              <TableCell key={header.id}>
                {isActionColumn ? (
                  <div className="flex items-center justify-end gap-2">
                    <Skeleton className="h-4 w-4 dark:bg-slate-500" />
                    <Skeleton className="h-4 w-4 dark:bg-slate-500" />
                  </div>
                ) : meta?.skeleton?.customCell ? (
                  meta.skeleton.customCell()
                ) : (
                  <Skeleton
                    className="h-4 dark:bg-slate-500"
                    style={{
                      width: Math.floor(Math.random() * 81 + 20) + '%',
                    }}
                  />
                )}
              </TableCell>
            )
          })}
        </TableRow>
      )),
    )
  }, [table])

  return (
    <div
      className={classNames(
        'flex w-full flex-col overflow-auto',
        wrapperClassName,
      )}
    >
      {(title || enableSearch || enableExport || extra) && (
        <div className="flex flex-col justify-between gap-4 pb-4 md:flex-row">
          {title && (
            <h2
              className={classNames(
                'text-secondary-500 text-2xl font-semibold dark:text-white',
                titleClassName,
              )}
            >
              {title}
            </h2>
          )}
          {/* Check if searchQuery is undefined to render the search input, since a defined searchQuery means
              the parent component is controlling the search state externally */}
          {((enableSearch && searchQuery === undefined) ||
            enableExport ||
            extra) && (
            <div className="flex flex-col items-center gap-2 sm:flex-row md:ml-auto">
              <div className="actions-wrapper flex gap-2">
                {enableExport && exportOptions && (
                  <Export
                    {...exportOptions}
                    disabled={!data.length}
                  />
                )}
                {enableSearch && searchQuery === undefined && (
                  <SearchInput
                    placeholder="Search"
                    onInputChange={(value) =>
                      table.getColumn(searchColumn)?.setFilterValue(value)
                    }
                  />
                )}
              </div>
              {extra}
            </div>
          )}
        </div>
      )}
      <div className={classNames('bg-card rounded-2xl p-4', className)}>
        <Table
          stickyHeaderSizeProp={stickyHeaderSize}
          testId={testId}
        >
          <colgroup>
            {table
              .getRowModel()
              .rows?.[0]?.getVisibleCells()
              .map((cell) => (
                <col
                  key={cell.id}
                  style={{
                    width: cell.column.columnDef.size
                      ? `${cell.column.columnDef.size}px`
                      : undefined,
                  }}
                />
              ))}
          </colgroup>
          <TableHeader
            className={classNames({
              'sticky top-0 bg-[#ffffff] opacity-100 dark:bg-[#232634]':
                !!stickyHeaderSize,
            })}
          >
            {renderHeader}
          </TableHeader>
          <TableBody
            className={classNames({
              'h-60': tbodyMinHeight,
            })}
          >
            {isLoading ? renderLoadingSkeleton : renderRows}
            {footerRow && (
              <TableRow>
                <TableCell colSpan={columns.length}>{footerRow}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {footer !== undefined && <div className="mt-4">{footer}</div>}

      {enablePagination && (
        <DataTablePagination
          table={table}
          type={paginationVariant}
          className={footer !== undefined ? 'mt-auto' : ''}
          disabled={pageCount === 0}
        />
      )}
    </div>
  )
}

export const DataTable = memo(_DataTable) as typeof _DataTable
